import { process_ } from "./filters";
import { HOTELS_INFO } from "../configuration";

const ACTIVE_FILTERS_LINE = "ACTIVE_FILTERS_LINE";
const BREAK_LINE = "BREAK_LINE";
const GRID_HORIZONTAL = "GRID_HORIZONTAL";
const TABS = "TABS";

const getReportTitle = (report, fallback) =>
    report.title ?? report.props?.title ?? fallback;

const buildSourceId = (pathParts) => pathParts.join("-");

const canShowVoucherReports = (hotelID) => {
    const hides = (HOTELS_INFO[hotelID] ?? {}).hide ?? {};
    return !(hides.Vouchers ?? 0);
};

const resolveReportJs = ({ report, js, jsOverrides = {} }) => {
    const mergedFilters = report.filters
        ? { ...js.filters, ...report.filters }
        : js.filters;

    return {
        ...js,
        dataType: report.dataType ?? js.dataType,
        presetDate: report.presetDate ?? js.presetDate,
        fromDay: report.fromDay ?? js.fromDay,
        toDay: report.toDay ?? js.toDay,
        groupBy: report.groupBy ?? js.groupBy,
        filters: mergedFilters,
        fieldsOrder: report.fieldsOrder ?? js.fieldsOrder,
        hideFields: report.hideFields ?? js.hideFields,
        ...jsOverrides,
        filters: jsOverrides.filters
            ? { ...mergedFilters, ...jsOverrides.filters }
            : mergedFilters,
    };
};

export const flattenReportsForExcel = ({ reports = [], hotelID }) => {
    const showVouchers = canShowVoucherReports(hotelID);

    const visit = (report, pathParts = [], meta = {}) => {
        if (!report) return [];

        switch (report.type) {
            case BREAK_LINE:
            case ACTIVE_FILTERS_LINE:
                return [];

            case GRID_HORIZONTAL:
                return (report.reports ?? []).flatMap((child, index) =>
                    visit(child, [...pathParts, `grid-${index}`], meta),
                );

            case TABS:
                return (report.tabs ?? [])
                    .filter(
                        (tab) => !((tab.isVoucher ?? false) && !showVouchers),
                    )
                    .flatMap((tab, tabIndex) =>
                        (tab.reports ?? []).flatMap((child, childIndex) =>
                            visit(
                                child,
                                [
                                    ...pathParts,
                                    `tab-${tabIndex}`,
                                    `report-${childIndex}`,
                                ],
                                {
                                    ...meta,
                                    groupTitle: tab.title,
                                },
                            ),
                        ),
                    );

            default:
                return [
                    {
                        report,
                        sourceId: buildSourceId(pathParts),
                        title: getReportTitle(
                            report,
                            `Report ${pathParts[pathParts.length - 1] ?? 0}`,
                        ),
                        groupTitle: meta.groupTitle ?? null,
                    },
                ];
        }
    };

    return reports.flatMap((report, index) =>
        visit(report, [`report-${index}`]),
    );
};

const resolveReportExportContext = ({
    item,
    js,
    hotelID,
    dataBookings,
    dataSimple,
    dataTargets,
    cache,
    extraContext,
}) => {
    const { report } = item;
    const jsResolved = resolveReportJs({ report, js });
    const dataBookingsToUse = report.dataBookingsOverride ?? dataBookings;
    const baseContext = {
        ...item,
        report,
        js: jsResolved,
        hotelID,
        dataBookings: dataBookingsToUse,
        dataSimple,
        dataTargets,
        extraContext,
    };

    if (report.skipExcelDataProcessing) {
        return {
            ...baseContext,
            data: {},
        };
    }

    const cacheKey = [
        dataBookingsToUse.hotelID,
        report.cacheKey ?? report.title ?? report.type,
        JSON.stringify(jsResolved),
        dataBookingsToUse.status,
        dataBookingsToUse.updatedAt ?? "",
    ].join("|");

    const cached = cache.get(cacheKey);
    if (cached) {
        return {
            ...baseContext,
            js: cached.js,
            data: cached.data,
        };
    }

    const processor = report.customProcess ?? process_;
    const processed = processor({
        js: jsResolved,
        dataBookings: dataBookingsToUse,
        hotelID,
        dataSimple,
        dataTargets,
    });

    const resolved = {
        js: jsResolved,
        data: processed.entries,
    };

    cache.set(cacheKey, resolved);

    return {
        ...baseContext,
        js: resolved.js,
        data: resolved.data,
    };
};

const normalizeSections = (sections, item) => {
    const array = Array.isArray(sections) ? sections : [sections];

    return array.filter(Boolean).map((section, index) => ({
        ...section,
        id: section.id ?? `${item.sourceId}-${index}`,
        title: section.title ?? item.title,
        source: {
            id: item.sourceId,
            type: item.report.type,
            title: item.title,
            groupTitle: item.groupTitle,
        },
    }));
};

const translateReportToSections = ({ item, context, adapters }) => {
    const translator = item.report.exportExcel ?? adapters[item.report.type];

    if (!translator) return [];

    return normalizeSections(translator(context), item);
};

export const buildExcelSectionsFromReports = ({
    reports = [],
    js,
    hotelID,
    dataBookings,
    dataSimple,
    dataTargets,
    extraContext = {},
    adapters = {},
}) => {
    const items = flattenReportsForExcel({ reports, hotelID });
    const cache = new Map();

    return items.reduce(
        (acc, item) => {
            const context = resolveReportExportContext({
                item,
                js,
                hotelID,
                dataBookings,
                dataSimple,
                dataTargets,
                cache,
                extraContext,
            });

            const sections = translateReportToSections({
                item,
                context,
                adapters,
            });

            if (sections.length === 0) {
                acc.skipped.push({
                    id: item.sourceId,
                    type: item.report.type,
                    title: item.title,
                    groupTitle: item.groupTitle,
                });
                return acc;
            }

            acc.sections.push(...sections);
            return acc;
        },
        { sections: [], skipped: [] },
    );
};
